import { useId, useState } from 'react'
import {
  Avatar, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  LinearProgress, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import { LineChart } from '@mui/x-charts/LineChart'
import { PieChart } from '@mui/x-charts/PieChart'
import { BarChart } from '@mui/x-charts/BarChart'

import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import ArrowOutwardOutlinedIcon from '@mui/icons-material/ArrowOutwardOutlined'
import AddIcon from '@mui/icons-material/Add'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { Link as RouterLink } from 'react-router-dom'
import { PageShell } from '../../layouts/PageShell'
import { useAuthStore } from '../../store/authStore'
import { useOrgSettings } from '../../hooks/useOrgSettings'

// Shared tooltip styling — adapts to light/dark mode instead of forcing dark
const chartTooltipSx = {
  '& .MuiChartsTooltip-root, & .MuiChartsTooltip-paper': {
    bgcolor: 'background.paper',
    color: 'text.primary',
    borderRadius: '10px',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: (t: any) => t.palette.mode === 'light'
      ? '0 8px 24px -6px rgba(15,23,42,0.14)'
      : '0 8px 24px -6px rgba(0,0,0,0.4)',
    fontSize: 12,
    padding: '8px 12px',
    backdropFilter: 'blur(8px)',
  },
  '& .MuiChartsTooltip-table': { color: 'inherit' },
  '& .MuiChartsTooltip-cell':  { color: 'inherit', padding: '2px 6px', fontSize: 12 },
  '& .MuiChartsTooltip-labelCell': { fontWeight: 500, color: 'text.secondary' },
  '& .MuiChartsTooltip-valueCell':  { fontWeight: 600, color: 'text.primary' },
  '& .MuiChartsTooltip-mark':       { borderRadius: 99 },
} as const

import { useDashboard, EMPTY_KPIS } from './dummy'
import { useLocation, useNavigate } from 'react-router-dom'
import { DetailedOverview } from './DetailedOverview'
import { ChecklistOverview } from './ChecklistOverview'
import { TicketsOverview } from './TicketsOverview'
import { MyOverview } from './MyOverview'
import { FeedbackOverview } from './FeedbackOverview'
import { DashboardDownload } from './DashboardDownload'
import { OverviewMapSection } from './components/OverviewMapSection'
import { usePermissions } from '../../hooks/usePermissions'
import { useEffect, useRef } from 'react'

type OverviewTab = 'summary' | 'detailed' | 'checklists' | 'tickets' | 'my' | 'feedback'

export function OverviewPage() {
  const user = useAuthStore((s) => s.user)
  const { formatCurrency } = useOrgSettings()
  const dashQ = useDashboard()
  const d = dashQ.data
  const kpis = d?.kpis ?? EMPTY_KPIS

  // Each tab is gated by its own permission. Anyone signed in as a
  // super_admin sees all three by definition. For org users we honour
  // the role's grants — missing perm → tab is hidden + redirected
  // away from if directly URL-hit.
  const { can } = usePermissions()
  const me = useAuthStore((s) => s.user)
  const isSuperAdmin = me?.type === 'super_admin'
  const canSeeGeneral    = isSuperAdmin || can('overview:read_general')
  // Temporarily force-hidden per product decision — customers found the
  // extra tab confusing. The feature, its route, and its permission are
  // otherwise untouched; delete this line (restoring the check below)
  // to bring the tab back.
  const canSeeDetailed   = false // isSuperAdmin || can('overview:read_detailed')
  const canSeeChecklists = isSuperAdmin || can('overview:read_checklists')
  const canSeeTickets    = isSuperAdmin || can('overview:read_tickets')
  const canSeeMy         = isSuperAdmin || can('overview:read_my')
  // Customer feedback KPIs — standalone gate, same reasoning as the
  // map widget below: satisfaction data is more sensitive than
  // operational compliance %, so only org_admin + customer_admin see
  // it by default.
  const canSeeFeedback   = isSuperAdmin || can('overview:read_feedback')
  // The embedded sites map is an admin-only widget by default
  // (org_admin + customer_admin via seeded role-permissions). Other
  // roles can be granted view access from Settings → Roles.
  const canSeeMap      = isSuperAdmin || can('overview:read_map')

  // Route-driven tab selection so deep links + browser back/forward
  // keep working — /overview/detailed → detailed, /overview/my → my,
  // /overview → general. Tab clicks just navigate; the URL stays the
  // single source of truth.
  const location = useLocation()
  const navigate = useNavigate()
  const urlTab: OverviewTab = location.pathname.startsWith('/overview/detailed')
    ? 'detailed'
    : location.pathname.startsWith('/overview/checklists')
    ? 'checklists'
    : location.pathname.startsWith('/overview/tickets')
    ? 'tickets'
    : location.pathname.startsWith('/overview/my')
    ? 'my'
    : location.pathname.startsWith('/overview/feedback')
    ? 'feedback'
    : 'summary'

  // Redirect the user to the first tab they CAN see if they hit a URL
  // they don't have access to. Falls through to a no-access message
  // when they have none of the perms.
  const accessibleTabs: OverviewTab[] = [
    canSeeGeneral    && 'summary',
    canSeeDetailed   && 'detailed',
    canSeeChecklists && 'checklists',
    canSeeTickets    && 'tickets',
    canSeeMy         && 'my',
    canSeeFeedback   && 'feedback',
  ].filter(Boolean) as OverviewTab[]
  const activeTab: OverviewTab = accessibleTabs.includes(urlTab)
    ? urlTab
    : (accessibleTabs[0] ?? 'summary')

  useEffect(() => {
    if (urlTab !== activeTab) {
      const path =
        activeTab === 'detailed'   ? '/overview/detailed'
        : activeTab === 'checklists' ? '/overview/checklists'
        : activeTab === 'tickets'  ? '/overview/tickets'
        : activeTab === 'my'      ? '/overview/my'
        : activeTab === 'feedback' ? '/overview/feedback'
        : '/overview'
      navigate(path, { replace: true })
    }
  }, [urlTab, activeTab, navigate])

  // Ref handed to the download button — captures whatever is rendered
  // inside this node. Switching tabs swaps the children so the same
  // ref always points at the current tab's content.
  const dashboardRef = useRef<HTMLDivElement | null>(null)
  // Filenames mirror the tab so saved exports stay self-explanatory.
  const downloadStem =
    activeTab === 'detailed'     ? `detailed-overview-${new Date().toISOString().slice(0, 10)}`
    : activeTab === 'checklists' ? `checklist-overview-${new Date().toISOString().slice(0, 10)}`
    : activeTab === 'tickets'    ? `tickets-overview-${new Date().toISOString().slice(0, 10)}`
    : activeTab === 'my'         ? `my-overview-${new Date().toISOString().slice(0, 10)}`
    : activeTab === 'feedback'   ? `feedback-overview-${new Date().toISOString().slice(0, 10)}`
    : `overview-${new Date().toISOString().slice(0, 10)}`

  const greeting = (() => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 18) return 'Good afternoon'
    return 'Good evening'
  })()

  const tabsToolbar = accessibleTabs.length > 1 ? (
    <Tabs
      value={activeTab}
      onChange={(_, v) => {
        const path = v === 'detailed' ? '/overview/detailed'
          : v === 'checklists' ? '/overview/checklists'
          : v === 'tickets' ? '/overview/tickets'
          : v === 'my' ? '/overview/my'
          : v === 'feedback' ? '/overview/feedback'
          : '/overview'
        navigate(path)
      }}
      variant="standard"
      sx={{
        minHeight: 38,
        '& .MuiTab-root': {
          minHeight: 38, textTransform: 'none', fontSize: 13, fontWeight: 600,
          letterSpacing: '-0.005em', color: 'text.secondary', px: 0, mr: 3.5,
          '&.Mui-selected': { color: 'primary.main' },
        },
        '& .MuiTabs-indicator': { height: 2 },
      }}
    >
      {canSeeGeneral    && <Tab value="summary"    label="General Overview" />}
      {canSeeDetailed   && <Tab value="detailed"   label="Detailed Overview" />}
      {canSeeChecklists && <Tab value="checklists" label="Checklist Overview" />}
      {canSeeTickets    && <Tab value="tickets"    label="Tickets Overview" />}
      {canSeeMy         && <Tab value="my"         label="My Overview" />}
      {canSeeFeedback   && <Tab value="feedback"   label="Feedback Overview" />}
    </Tabs>
  ) : undefined

  // Tabs render unconditionally; the inner content branches. Summary
  // shows its own loading spinner so the tab row stays visible while
  // the dashboard fetch is in flight.
  return (
    <PageShell
      title="Overview"
      subtitle={
        activeTab === 'detailed'
          ? 'Interactive compliance dashboard — drill into work-order and checklist performance across sites, locations, people, and time.'
        : activeTab === 'checklists'
          ? 'The same compliance dashboard, scoped to checklists and tasks only.'
        : activeTab === 'tickets'
          ? 'Live view of every site with active requests, broken down by issue template and work-order conversion.'
        : activeTab === 'my'
          ? `Your personal performance dashboard — score, streaks, and badges to keep momentum going.`
        : activeTab === 'feedback'
          ? 'Customer feedback KPIs (fNPS, CSI, satisfaction) from MPulse-linked assets — filter by date, time, and site.'
        : `${greeting}, ${user?.name?.split(' ')[0] ?? 'there'} — here's where your operation stands today.`
      }
      toolbar={tabsToolbar}
      actions={
        <Stack direction="row" spacing={1.25} alignItems="center" data-exclude="true">
          <DashboardDownload
            targetRef={dashboardRef}
            filenameStem={downloadStem}
            disabled={accessibleTabs.length === 0
              || (activeTab === 'summary' && dashQ.isLoading)}
          />
          {activeTab === 'summary' && (
            <Button
              component={RouterLink}
              to="/work-orders"
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ borderRadius: 999 }}
            >
              New work order
            </Button>
          )}
        </Stack>
      }
    >
      {/* Single ref'd container around the tab content so the download
          button can screenshot whichever tab is active without needing
          to know about each component. `data-exclude` skips this node
          if a nested DashboardDownload is ever rendered (defensive). */}
      <Box ref={dashboardRef}>
      {accessibleTabs.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
            No overview access
          </Typography>
          <Typography sx={{ fontSize: 13 }}>
            Your role doesn't have any overview permission. Ask an admin to
            grant one of <code>overview:read_general</code>, <code>overview:read_detailed</code>,
            <code>overview:read_checklists</code>, <code>overview:read_tickets</code>,
            <code>overview:read_my</code>, or <code>overview:read_feedback</code>.
          </Typography>
        </Box>
      ) : activeTab === 'detailed' ? (
        <DetailedOverview />
      ) : activeTab === 'checklists' ? (
        <ChecklistOverview />
      ) : activeTab === 'tickets' ? (
        <TicketsOverview />
      ) : activeTab === 'my' ? (
        <MyOverview />
      ) : activeTab === 'feedback' ? (
        <FeedbackOverview />
      ) : dashQ.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
      <Stack spacing={3}>
      {/* ─── Row 1 — Hero KPIs ─── */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WorkKindSummaryCard
            label="Work orders"
            bucket={kpis.workOrders}
            tone="#EEF2FF"
            accent="#0D9488"
            icon={BuildOutlinedIcon}
            href="/work-orders"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <WorkKindSummaryCard
            label="Tasks"
            bucket={kpis.tasks}
            tone="#EEF2FF"
            accent="#0D9488"
            icon={AssignmentTurnedInOutlinedIcon}
            href="/tasks"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiCard
            label="Critical assets"
            value={kpis.criticalAssets}
            trend={{ dir: kpis.trends.critical >= 0 ? 'up' : 'down', pct: Math.abs(kpis.trends.critical), note: kpis.trends.critical > 0 ? 'need action' : 'stable', isGood: kpis.trends.critical <= 0 }}
            tone="#FEF2F2"
            accent="#DC2626"
            icon={WarningAmberOutlinedIcon}
            spark={kpis.sparklines.criticalAssets}
            href="/assets?criticality=critical"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <KpiCard
            label="Compliance rate"
            value={`${kpis.complianceRate}%`}
            trend={{ dir: 'up', pct: kpis.complianceRate > 0 ? Math.min(kpis.complianceRate, 100) - 90 : 0, note: 'PMs on time', isGood: true }}
            tone="#ECFDF5"
            accent="#059669"
            icon={VerifiedOutlinedIcon}
            spark={[kpis.complianceRate]}
            href="/reports"
            footer="Target: 95%"
          />
        </Grid>
      </Grid>

      {/* ─── Row 1.5 — Sites map (permission-gated) ───
          Embedded interactive map showing every site, with the same
          filter + colour-coding UX as the Locations page. Clicking a
          pin opens a per-site overview drawer (locations / assets /
          inventory / people / work-order breakdown / top performers).
          Visible only to roles with `overview:read_map` — seeded for
          org_admin + customer_admin by default. */}
      {canSeeMap && <OverviewMapSection />}

      {/* ─── Row 2 — Action items (what needs attention) ─── */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <UpcomingCard
            maintenance={d?.upcomingMaintenance ?? []}
            warranties={d?.expiringWarranties ?? []}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <AssetHealthCard health={d?.assetHealth ?? []} />
        </Grid>
      </Grid>

      {/* ─── Row 3 — Trends & distribution ─── */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <WorkOrderTrendCard />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <AssetStatusCard data={d?.assetStatus ?? []} />
        </Grid>
      </Grid>

      {/* ─── Row 4 — Activity + Technicians + Cost ─── */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <RecentActivityCard activity={d?.recentActivity ?? []} />
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <TopTechniciansCard techs={d?.topTechnicians ?? []} />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <CostSummaryCard cost={d?.costSummary} />
        </Grid>
      </Grid>
      </Stack>
      )}
      </Box>
    </PageShell>
  )
}

// ─── KPI Card with sparkline ─────────────────────────────────────────────────

interface KpiCardProps {
  label: string
  value: number | string
  trend: { dir: 'up' | 'down'; pct: number; note: string; isGood: boolean }
  tone: string
  accent: string
  icon: React.ElementType
  spark: number[]
  href?: string
  footer?: string
}

function KpiCard({
  label, value, trend, tone, accent, icon: Icon, spark, href, footer,
}: KpiCardProps) {

  const content = (
    <Card
      sx={{
        height: '100%',
        cursor: href ? 'pointer' : 'default',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': href
          ? {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 14px -8px rgba(15,23,42,0.12)',
              borderColor: 'rgba(0,0,0,0.12)',
            }
          : undefined,
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
          <Avatar
            variant="rounded"
            sx={{ width: 38, height: 38, bgcolor: tone, color: accent }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Avatar>
          {href && (
            <ArrowOutwardOutlinedIcon
              sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.45 }}
            />
          )}
        </Stack>

        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: '-0.005em',
            mb: 0.75,
          }}
        >
          {label}
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              color: 'text.primary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {value}
          </Typography>
          <Chip
            label={
              <Stack direction="row" alignItems="center" spacing={0.25}>
                {trend.dir === 'up'
                  ? <ArrowUpwardIcon sx={{ fontSize: 11 }} />
                  : <ArrowDownwardIcon sx={{ fontSize: 11 }} />}
                <span>{trend.pct}%</span>
              </Stack>
            }
            size="small"
            sx={{
              height: 20,
              fontSize: 11,
              fontWeight: 700,
              bgcolor: trend.isGood ? '#ECFDF5' : '#FEF2F2',
              color: trend.isGood ? '#059669' : '#DC2626',
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Stack>

        {/* Sparkline */}
        <Box sx={{ height: 34, mb: 1.25 }}>
          <InlineSparkline data={spark} color={accent} />
        </Box>

        {footer && (
          <Typography
            sx={{ color: 'text.secondary', fontSize: 12, letterSpacing: '-0.005em' }}
          >
            {footer}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
  return href ? <RouterLink to={href} style={{ textDecoration: 'none' }}>{content}</RouterLink> : content
}

interface WorkKindBucket {
  total: number
  open: number
  overdue: number
  inProgress: number
  resolved: number
  closed: number
  scheduled: number
}

interface WorkKindSummaryCardProps {
  label: string
  bucket: WorkKindBucket
  tone: string
  accent: string
  icon: React.ElementType
  href: string
}

/// Total up top, then a clean grid of five mutually-legible buckets
/// that add up to it exactly — replaces the old "Open work orders" /
/// "Overdue work orders" pair, which mixed Tasks into a card labelled
/// Work Orders (both share the work_orders table via `kind`) and only
/// ever surfaced two of the statuses that actually matter here.
///
/// Overdue is deliberately NOT one of the five grid tiles — it's a
/// status of Open/In progress (a flag on top of them), not a disjoint
/// bucket, so folding it into the same grid as a peer made the five
/// visible numbers look like they should sum to Total and quietly
/// not. It's rendered instead as a separate red badge next to Total,
/// which reads unambiguously as "N of these are overdue" rather than
/// "here's a 6th bucket."
function WorkKindSummaryCard({ label, bucket, tone, accent, icon: Icon, href }: WorkKindSummaryCardProps) {
  const stats: Array<{ label: string; value: number; color: string }> = [
    { label: 'Open',         value: bucket.open,       color: '#0D9488' },
    { label: 'In progress',  value: bucket.inProgress, color: '#D97706' },
    { label: 'Resolved',     value: bucket.resolved,   color: '#059669' },
    { label: 'Closed',       value: bucket.closed,     color: '#8B5CF6' },
    { label: 'Scheduled',    value: bucket.scheduled,  color: '#0EA5E9' },
  ]

  return (
    <RouterLink to={href} style={{ textDecoration: 'none' }}>
      <Card
        sx={{
          height: '100%',
          cursor: 'pointer',
          transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 14px -8px rgba(15,23,42,0.12)',
            borderColor: 'rgba(0,0,0,0.12)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
            <Avatar variant="rounded" sx={{ width: 38, height: 38, bgcolor: tone, color: accent }}>
              <Icon sx={{ fontSize: 19 }} />
            </Avatar>
            <ArrowOutwardOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.45 }} />
          </Stack>

          <Typography sx={{ color: 'text.secondary', fontSize: 13, fontWeight: 500, letterSpacing: '-0.005em', mb: 0.75 }}>
            {label}
          </Typography>

          <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.5 }}>
            <Typography sx={{
              fontSize: 28, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1,
              color: 'text.primary', fontVariantNumeric: 'tabular-nums',
            }}>
              {bucket.total.toLocaleString()}
            </Typography>
            {bucket.overdue > 0 && (
              <Chip
                label={`${bucket.overdue.toLocaleString()} overdue`}
                size="small"
                sx={{
                  height: 20, fontSize: 11, fontWeight: 700,
                  bgcolor: '#FEF2F2', color: '#DC2626',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
          </Stack>

          <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1, rowGap: 0.75,
            pt: 1.25, borderTop: '1px solid', borderColor: 'divider',
          }}>
            {stats.map((s) => (
              <Stack key={s.label} direction="row" alignItems="baseline" spacing={0.5}>
                <Typography sx={{
                  fontSize: 14, fontWeight: 700, color: s.color, lineHeight: 1,
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {s.value.toLocaleString()}
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: 'text.secondary', fontWeight: 500 }}>
                  {s.label}
                </Typography>
              </Stack>
            ))}
          </Box>
        </CardContent>
      </Card>
    </RouterLink>
  )
}

// ─── Work order trend chart ──────────────────────────────────────────────────

const COLOR_COMPLETED = '#6366F1'
const COLOR_CREATED   = '#94A3B8'

function WorkOrderTrendCard() {
  const [range, setRange] = useState<'7d' | '30d' | '90d'>('30d')
  const days = range === '7d' ? 7 : range === '90d' ? 90 : 30
  const dashQ = useDashboard(days)
  const trend = dashQ.data?.woTrend

  const trendDays = trend?.days ?? []
  const created   = trend?.created ?? []
  const completed = trend?.completed ?? []

  const totalCreated   = created.reduce((a, b) => a + b, 0)
  const totalCompleted = completed.reduce((a, b) => a + b, 0)
  const completionRate = totalCreated ? Math.round((totalCompleted / totalCreated) * 100) : 0
  const avgPerDay      = created.length ? (totalCreated / created.length).toFixed(1) : '0'
  const backlog        = totalCreated - totalCompleted
  const peakIndex      = created.reduce((bestI, v, i) => (v > (created[bestI] ?? 0) ? i : bestI), 0)
  const peakValue      = created[peakIndex] ?? 0
  const peakLabel      = trendDays[peakIndex] ?? '—'

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'flex-start' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
              Work order activity
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}>
              Created vs completed over time
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <CornerTile label="Completed" value={totalCompleted} accent={COLOR_COMPLETED} />
            <CornerTile label="Rate"      value={`${completionRate}%`} accent="#10B981" />
          </Stack>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <Stack direction="row" spacing={2.5}>
            <Legend color={COLOR_COMPLETED} label="Completed" value={totalCompleted} />
            <Legend color={COLOR_CREATED}   label="Created"   value={totalCreated} />
          </Stack>
          <Tabs
            value={range}
            onChange={(_, v) => setRange(v)}
            sx={{ minHeight: 28, '& .MuiTab-root': { minHeight: 28, px: 1.25, fontSize: 12 } }}
          >
            <Tab value="7d"  label="7d" />
            <Tab value="30d" label="30d" />
            <Tab value="90d" label="90d" />
          </Tabs>
        </Stack>

        <Box sx={{ height: 240 }}>
          {trendDays.length > 0 ? (
            <LineChart
              series={[
                {
                  data: completed,
                  label: 'Completed',
                  color: COLOR_COMPLETED,
                  area: true,
                  showMark: false,
                  curve: 'monotoneX',
                  valueFormatter: (v) => `${v} completed`,
                },
                {
                  data: created,
                  label: 'Created',
                  color: COLOR_CREATED,
                  area: false,
                  showMark: false,
                  curve: 'monotoneX',
                  valueFormatter: (v) => `${v} created`,
                },
              ]}
              xAxis={[{ scaleType: 'point', data: trendDays }]}
              margin={{ top: 10, right: 16, bottom: 24, left: 32 }}
              hideLegend
              axisHighlight={{ x: 'line' }}
              sx={{
                '& .MuiAreaElement-series-Completed': { fill: 'url(#woTrendFill)' },
                '& .MuiLineElement-series-Completed': { strokeWidth: 2 },
                '& .MuiLineElement-series-Created':   { strokeWidth: 1.5, strokeDasharray: '4 3' },
                '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'transparent' },
                '& .MuiChartsAxis-tickLabel': {
                  fill: (t) => t.palette.text.secondary,
                  fontSize: 11,
                },
                '& .MuiChartsGrid-line': {
                  stroke: (t) => t.palette.divider,
                  strokeDasharray: '2 4',
                },
                '& .MuiChartsAxisHighlight-root': {
                  stroke: (t) => t.palette.text.disabled,
                  strokeDasharray: '2 3',
                  strokeWidth: 1,
                },
                ...chartTooltipSx,
              }}
            >
              <defs>
                <linearGradient id="woTrendFill" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%"   stopColor={COLOR_COMPLETED} stopOpacity="0.18" />
                  <stop offset="60%"  stopColor={COLOR_COMPLETED} stopOpacity="0.04" />
                  <stop offset="100%" stopColor={COLOR_COMPLETED} stopOpacity="0" />
                </linearGradient>
              </defs>
            </LineChart>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>No data yet</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mt: 2.5, mb: 0 }} />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          divider={<Divider orientation="vertical" flexItem />}
          sx={{ pt: 2 }}
        >
          <MiniStat label="Daily average" value={avgPerDay}       hint="WOs created" />
          <MiniStat label="Peak day"      value={String(peakValue)} hint={peakLabel} />
          <MiniStat
            label="Completion rate"
            value={`${completionRate}%`}
            hint={`${totalCompleted} of ${totalCreated}`}
            good={completionRate >= 85}
          />
          <MiniStat
            label="Backlog"
            value={backlog > 0 ? `+${backlog}` : String(backlog)}
            hint={`over last ${range}`}
            good={backlog <= 0}
          />
        </Stack>
      </CardContent>
    </Card>
  )
}

function CornerTile({ label, value, accent }: { label: string; value: string | number; accent: string }) {
  return (
    <Box
      sx={{
        px: 1.5, py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: (t) => t.palette.mode === 'light' ? '#FAFAFA' : 'rgba(255,255,255,0.03)',
        minWidth: 90,
      }}
    >
      <Typography
        sx={{
          fontSize: 10.5, fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 6, height: 6, borderRadius: 99, bgcolor: accent }} />
        <Typography
          sx={{
            fontSize: 16, fontWeight: 700,
            letterSpacing: '-0.015em',
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {value}
        </Typography>
      </Stack>
    </Box>
  )
}

function Legend({ color, label, value }: { color: string; label: string; value: number }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: color }} />
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Stack>
  )
}

function MiniStat({
  label, value, hint, good,
}: { label: string; value: string; hint?: string; good?: boolean }) {
  const color = good === undefined ? 'text.primary' : good ? '#059669' : '#DC2626'
  return (
    <Box sx={{ flex: 1, px: { sm: 2.5 }, py: { xs: 1, sm: 0 } }}>
      <Typography
        sx={{
          fontSize: 10.5, fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 18, fontWeight: 700,
          letterSpacing: '-0.015em',
          lineHeight: 1.1,
          fontVariantNumeric: 'tabular-nums',
          color,
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>
          {hint}
        </Typography>
      )}
    </Box>
  )
}

// ─── Asset status donut ──────────────────────────────────────────────────────

function AssetStatusCard({ data }: { data: Array<{ label: string; value: number; color: string }> }) {
  const total = data.reduce((a, b) => a + b.value, 0)

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
          Asset status
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
          Distribution across your fleet
        </Typography>

        <Box sx={{ height: 180, position: 'relative' }}>
          {data.length > 0 ? (
            <PieChart
              series={[
                {
                  data: data.map((s, i) => ({ id: i, value: s.value, label: s.label, color: s.color })),
                  innerRadius: 50,
                  outerRadius: 78,
                  paddingAngle: 2,
                  cornerRadius: 3,
                },
              ]}
              height={180}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              hideLegend
              sx={chartTooltipSx}
            />
          ) : null}
          <Box
            sx={{
              position: 'absolute', inset: 0,
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            <Typography
              sx={{
                fontSize: 28, fontWeight: 700,
                letterSpacing: '-0.03em', lineHeight: 1,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {total}
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
              Total assets
            </Typography>
          </Box>
        </Box>

        <Stack spacing={0.75} sx={{ mt: 2 }}>
          {data.map((s) => (
            <Stack
              key={s.label}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: s.color }} />
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                  {s.label}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {s.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}

// ─── Asset health (bars) ─────────────────────────────────────────────────────

function AssetHealthCard({ health }: { health: Array<{ label: string; value: number; color: string }> }) {
  const alertCount = health.find(h => h.label === 'Critical')?.value ?? 0
  const attentionCount = health.find(h => h.label === 'Needs attention')?.value ?? 0
  const totalAlerts = alertCount + attentionCount

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
              Asset health
            </Typography>
            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.25 }}>
              Based on criticality classification
            </Typography>
          </Box>
          {totalAlerts > 0 && (
            <Chip
              label={`${totalAlerts} alerts`}
              size="small"
              sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontWeight: 700 }}
            />
          )}
        </Stack>

        <Stack spacing={2.5}>
          {health.map((h) => (
            <Box key={h.label}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: h.color }} />
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{h.label}</Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" spacing={0.75}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {h.value}%
                  </Typography>
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={h.value}
                sx={{
                  height: 8,
                  borderRadius: 99,
                  '& .MuiLinearProgress-bar': { bgcolor: h.color, borderRadius: 99 },
                }}
              />
            </Box>
          ))}
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        <Button
          component={RouterLink}
          to="/assets"
          endIcon={<ArrowOutwardOutlinedIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: 13, color: 'text.secondary' }}
        >
          View all assets
        </Button>
      </CardContent>
    </Card>
  )
}

// ─── Upcoming maintenance + warranties ───────────────────────────────────────

function UpcomingCard({ maintenance, warranties }: {
  maintenance: Array<{ asset: string; task: string; dueInDays: number; priority: string }>
  warranties: Array<{ asset: string; model: string; expiresInDays: number }>
}) {
  const [tab, setTab] = useState<'maintenance' | 'warranty'>('maintenance')

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Upcoming
          </Typography>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 30, '& .MuiTab-root': { minHeight: 30, px: 1.5, fontSize: 12 } }}
          >
            <Tab value="maintenance" label="Maintenance" />
            <Tab value="warranty" label="Warranties" />
          </Tabs>
        </Stack>

        <Stack divider={<Divider />}>
          {tab === 'maintenance'
            ? (maintenance.length > 0 ? maintenance.map((u) => (
                <Stack key={u.asset + u.task} direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{ bgcolor: 'action.hover', color: 'text.secondary', width: 36, height: 36 }}
                  >
                    <BuildOutlinedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
                      {u.asset}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                      {u.task}
                    </Typography>
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={u.priority}
                      size="small"
                      sx={{
                        textTransform: 'capitalize',
                        bgcolor:
                          u.priority === 'high' ? '#FFFBEB' :
                          u.priority === 'critical' ? '#FEF2F2' :
                          u.priority === 'medium' ? '#EEF2FF' : '#F3F4F6',
                        color:
                          u.priority === 'high' ? '#D97706' :
                          u.priority === 'critical' ? '#DC2626' :
                          u.priority === 'medium' ? '#0D9488' : '#6B7280',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12, color: u.dueInDays <= 5 ? '#DC2626' : 'text.secondary',
                        fontWeight: 600, minWidth: 72, textAlign: 'right',
                      }}
                    >
                      {u.dueInDays}d
                    </Typography>
                  </Stack>
                </Stack>
              )) : <EmptyRow label="No upcoming maintenance" />)
            : (warranties.length > 0 ? warranties.map((w) => (
                <Stack key={w.asset} direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
                  <Avatar
                    variant="rounded"
                    sx={{ bgcolor: 'action.hover', color: 'text.secondary', width: 36, height: 36 }}
                  >
                    <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
                      {w.asset}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                      {w.model}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12, fontWeight: 600,
                      color: w.expiresInDays <= 30 ? '#D97706' : 'text.secondary',
                      minWidth: 72, textAlign: 'right',
                    }}
                  >
                    {w.expiresInDays}d left
                  </Typography>
                </Stack>
              )) : <EmptyRow label="No expiring warranties" />)}
        </Stack>
      </CardContent>
    </Card>
  )
}

function EmptyRow({ label }: { label: string }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{label}</Typography>
    </Box>
  )
}

// ─── Recent activity ─────────────────────────────────────────────────────────

function RecentActivityCard({ activity }: { activity: Array<{ initials: string; name: string; action: string; object: string; time: string; color: string }> }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Recent activity
          </Typography>
          <Tooltip title="Live feed across your workspace">
            <FiberManualRecordIcon sx={{ fontSize: 8, color: '#10B981' }} />
          </Tooltip>
        </Stack>

        {activity.length > 0 ? (
          <Stack spacing={2}>
            {activity.map((a, i) => (
              <Stack key={i} direction="row" spacing={1.75} alignItems="flex-start">
                <Avatar
                  sx={{
                    width: 30, height: 30,
                    bgcolor: a.color,
                    fontSize: 11, fontWeight: 600,
                    letterSpacing: '-0.01em',
                    flexShrink: 0,
                    mt: 0.25,
                  }}
                >
                  {a.initials}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, letterSpacing: '-0.005em', lineHeight: 1.4 }}>
                    <Box component="span" sx={{ fontWeight: 600 }}>{a.name}</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{a.action}</Box>{' '}
                    <Box component="span" sx={{ fontWeight: 500 }}>{a.object}</Box>
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.15 }}>
                    {a.time}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        ) : (
          <EmptyRow label="No recent activity" />
        )}
      </CardContent>
    </Card>
  )
}

// ─── Top technicians ────────────────────────────────────────────────────────

function TopTechniciansCard({ techs }: { techs: Array<{ name: string; initials: string; wos: number; avgTime: string; color: string }> }) {
  const max = Math.max(...techs.map((t) => t.wos), 1)

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Top technicians
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>This month</Typography>
        </Stack>

        {techs.length > 0 ? (
          <Stack spacing={2}>
            {techs.map((t, i) => (
              <Box key={t.name}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
                  <Typography
                    sx={{
                      fontSize: 11, fontWeight: 700,
                      color: 'text.secondary',
                      width: 14,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {i + 1}
                  </Typography>
                  <Avatar
                    sx={{ width: 28, height: 28, bgcolor: t.color, fontSize: 11, fontWeight: 600 }}
                  >
                    {t.initials}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
                      {t.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {t.avgTime} avg
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {t.wos}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={(t.wos / max) * 100}
                  sx={{
                    height: 3.5,
                    borderRadius: 99,
                    ml: 5,
                    '& .MuiLinearProgress-bar': { bgcolor: t.color, borderRadius: 99 },
                  }}
                />
              </Box>
            ))}
          </Stack>
        ) : (
          <EmptyRow label="No completed work orders yet" />
        )}
      </CardContent>
    </Card>
  )
}

// ─── Cost summary ────────────────────────────────────────────────────────────

function CostSummaryCard({ cost }: { cost?: { thisMonth: number; lastMonth: number; series: number[]; labels: string[]; partsPct: number; laborPct: number } }) {
  const { formatCurrency } = useOrgSettings()
  const thisMonth = cost?.thisMonth ?? 0
  const lastMonth = cost?.lastMonth ?? 0
  const series = cost?.series ?? []
  const labels = cost?.labels ?? []
  const partsPct = cost?.partsPct ?? 0
  const laborPct = cost?.laborPct ?? 0

  const pctChange = lastMonth > 0 ? Math.round(((thisMonth - lastMonth) / lastMonth) * 100) : 0
  const up = pctChange > 0

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: 3.5 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em', mb: 0.5 }}>
          Maintenance cost
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 13, mb: 2 }}>
          Month to date
        </Typography>

        <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
          <Typography
            sx={{
              fontSize: 34, fontWeight: 700,
              letterSpacing: '-0.03em', lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatCurrency(thisMonth)}
          </Typography>
          {pctChange !== 0 && (
            <Chip
              label={
                <Stack direction="row" alignItems="center" spacing={0.25}>
                  {up ? <ArrowUpwardIcon sx={{ fontSize: 11 }} /> : <ArrowDownwardIcon sx={{ fontSize: 11 }} />}
                  <span>{Math.abs(pctChange)}%</span>
                </Stack>
              }
              size="small"
              sx={{
                height: 20, fontSize: 11, fontWeight: 700,
                bgcolor: up ? '#FEF2F2' : '#ECFDF5',
                color: up ? '#DC2626' : '#059669',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          )}
        </Stack>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
          vs {formatCurrency(lastMonth)} last month
        </Typography>

        {series.length > 0 && (
          <Box sx={{ height: 80, mx: -1 }}>
            <BarChart
              series={[{ data: series, color: '#0D9488' }]}
              xAxis={[{ scaleType: 'band', data: labels }]}
              height={80}
              margin={{ top: 6, right: 6, bottom: 14, left: 4 }}
              borderRadius={4}
              hideLegend
              sx={{
                '& .MuiChartsAxis-root .MuiChartsAxis-line, & .MuiChartsAxis-root .MuiChartsAxis-tick': {
                  display: 'none',
                },
                '& .MuiChartsAxis-tickLabel': { display: 'none' },
                ...chartTooltipSx,
              }}
            />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1.5}>
          <SplitRow label="Parts" pct={partsPct} amount={Math.round(thisMonth * partsPct / 100)} color="#059669" />
          {laborPct > 0 && <SplitRow label="Labor" pct={laborPct} amount={Math.round(thisMonth * laborPct / 100)} color="#0D9488" />}
        </Stack>
      </CardContent>
    </Card>
  )
}

function SplitRow({
  label, pct, amount, color,
}: { label: string; pct: number; amount: number; color: string }) {
  const { formatCurrency } = useOrgSettings()
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: color }} />
          <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>{label}</Typography>
        </Stack>
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography
            sx={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCurrency(amount)}
          </Typography>
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
            {pct}%
          </Typography>
        </Stack>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{
          height: 4,
          borderRadius: 99,
          '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 99 },
        }}
      />
    </Box>
  )
}

// ─── Inline SVG sparkline ────────────────────────────────────────────────────

function InlineSparkline({
  data, color, height = 34,
}: { data: number[]; color: string; height?: number }) {
  const gid = useId()
  if (data.length < 2) return null

  const w = 100
  const h = height
  const min = Math.min(...data)
  const max = Math.max(...data)
  const range = max - min || 1
  const pad = 2

  const points = data.map((v, i) => ({
    x: (i / (data.length - 1)) * w,
    y: pad + (1 - (v - min) / range) * (h - pad * 2),
  }))

  let line = `M${points[0]!.x.toFixed(2)},${points[0]!.y.toFixed(2)}`
  for (let i = 1; i < points.length; i++) {
    const p0 = points[i - 1]!
    const p1 = points[i]!
    const cp1x = p0.x + (p1.x - p0.x) / 3
    const cp2x = p0.x + (2 * (p1.x - p0.x)) / 3
    line += ` C${cp1x.toFixed(2)},${p0.y.toFixed(2)} ${cp2x.toFixed(2)},${p1.y.toFixed(2)} ${p1.x.toFixed(2)},${p1.y.toFixed(2)}`
  }
  const area = `${line} L${w},${h} L0,${h} Z`

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height: '100%', display: 'block' }}
    >
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"  stopColor={color} stopOpacity="0.16" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity="0.95" />
    </svg>
  )
}
